import { useEffect, useRef, type ReactElement } from 'react';

/** Tonos de la celebración: el verde del éxito y el dorado del dinero. */
const TONES = ['#B37B12', '#10704F', '#D9A441', '#3E9C77', '#E8CE8A'];

interface Piece {
    x: number;
    y: number;
    vx: number;
    vy: number;
    w: number;
    h: number;
    rot: number;
    vr: number;
    tone: string;
    life: number;
}

/** De dónde brota: del borde inferior del contenedor, o desde atrás de él hacia sus 4 lados. */
export type ConfettiOrigin = 'bottom' | 'behind';

/** Punto al azar sobre el perímetro de `r`, con su normal hacia afuera. */
function perimeterPoint(r: DOMRect): { x: number; y: number; nx: number; ny: number } {
    const t = Math.random() * 2 * (r.width + r.height);
    if (t < r.width) return { x: r.left + t, y: r.top, nx: 0, ny: -1 };
    if (t < r.width + r.height) return { x: r.right, y: r.top + t - r.width, nx: 1, ny: 0 };
    if (t < 2 * r.width + r.height)
        return { x: r.right - (t - r.width - r.height), y: r.bottom, nx: 0, ny: 1 };
    return { x: r.left, y: r.bottom - (t - 2 * r.width - r.height), nx: -1, ny: 0 };
}

/**
 * Ráfaga única de confeti. Con `bottom` cubre al padre `relative` y sale de abajo; con `behind`
 * el padre debe ser `isolate` (envuelve la tarjeta) y el confeti brota detrás de ella hacia afuera.
 *
 * Se dispara al montar y no vuelve. Con `prefers-reduced-motion` no dibuja nada.
 */
export function Confetti({
    pieces = 70,
    origin = 'bottom',
}: {
    pieces?: number;
    origin?: ConfettiOrigin;
}): ReactElement {
    const ref = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.max(1, Math.round(rect.width * dpr));
        canvas.height = Math.max(1, Math.round(rect.height * dpr));
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const behind = origin === 'behind';
        const anchor = canvas.parentElement?.getBoundingClientRect();
        const gravity = behind ? 0.09 : 0.22;
        const drag = behind ? 0.975 : 1;

        const flakes: Piece[] = Array.from({ length: pieces }, () => {
            const shape = {
                w: 3 + Math.random() * 4,
                h: 5 + Math.random() * 6,
                rot: Math.random() * Math.PI,
                vr: (Math.random() - 0.5) * 0.3,
                tone: TONES[Math.floor(Math.random() * TONES.length)] ?? '#B37B12',
                life: 1,
            };
            if (behind && anchor) {
                // Nace un poco adentro del borde (tapado por la tarjeta) y sale por la normal.
                const p = perimeterPoint(anchor);
                const speed = 3 + Math.random() * 6;
                const spread = (Math.random() - 0.5) * 1.2;
                return {
                    ...shape,
                    x: p.x - p.nx * 8,
                    y: p.y - p.ny * 8,
                    vx: (p.nx + spread * p.ny) * speed,
                    vy: (p.ny + spread * p.nx) * speed - 1,
                };
            }
            const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.2;
            const speed = 3 + Math.random() * 5;
            return {
                x: rect.width * (0.15 + Math.random() * 0.7),
                y: rect.height + 4,
                vx: Math.cos(angle) * speed,
                vy: Math.sin(angle) * speed,
                ...shape,
            };
        });

        let frame = 0;
        const draw = (): void => {
            ctx.clearRect(0, 0, rect.width, rect.height);
            let alive = 0;
            for (const p of flakes) {
                p.vx *= drag;
                p.vy = p.vy * drag + gravity;
                p.x += p.vx;
                p.y += p.vy;
                p.rot += p.vr;
                p.life -= 0.014;
                if (p.life <= 0) continue;
                alive += 1;
                ctx.save();
                ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
                ctx.translate(p.x, p.y);
                ctx.rotate(p.rot);
                ctx.fillStyle = p.tone;
                ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
                ctx.restore();
            }
            if (alive > 0) frame = requestAnimationFrame(draw);
        };
        frame = requestAnimationFrame(draw);

        return () => {
            cancelAnimationFrame(frame);
        };
    }, [pieces, origin]);

    // `behind` usa el viewport entero (fixed) para no crear scroll; -z-10 lo deja detrás de la tarjeta.
    const placement =
        origin === 'behind' ? 'fixed inset-0 -z-10 h-dvh w-screen' : 'absolute inset-0 size-full';
    return <canvas ref={ref} aria-hidden className={`pointer-events-none ${placement}`} />;
}
